import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { CARD_AUTHORIZATION_RECHECK_MS, fetchAwaitsCardAuthorization } from "@/hooks/usePerUseCardStatus";
import { parseCardAuthorizationStatus, type CardAuthorizationStatus } from "@/lib/card-authorization-grace";

export async function fetchCardAuthorizationStatus(orgId: string): Promise<CardAuthorizationStatus | null> {
  try {
    const { data, error } = await supabase.rpc("organization_card_authorization_status" as never, { p_org_id: orgId } as never);
    if (error) return null;
    return parseCardAuthorizationStatus(data);
  } catch {
    return null;
  }
}

export async function fetchLiveCardAuthorizationStatus(orgId: string): Promise<CardAuthorizationStatus | null> {
  const status = await fetchCardAuthorizationStatus(orgId);
  if (status) return status;
  const awaits = await fetchAwaitsCardAuthorization(orgId);
  if (awaits === null) return null;
  return { awaitsAuthorization: awaits, graceUntil: null, graceLastDay: null, aiPaused: awaits };
}

export function useCardAuthorizationStatus(orgId: string | null, enabled: boolean, reload = 0): CardAuthorizationStatus | null {
  const key = orgId && enabled ? `${orgId}:${reload}` : null;
  const [result, setResult] = useState<{ key: string; status: CardAuthorizationStatus | null } | null>(null);

  useEffect(() => {
    if (!key || !orgId) return;
    let mounted = true;
    void fetchCardAuthorizationStatus(orgId).then((status) => {
      if (mounted) setResult({ key, status });
    });
    return () => { mounted = false; };
  }, [key, orgId]);

  return key && result?.key === key ? result.status : null;
}

export function useLiveCardAuthorizationStatus(orgId: string | null, enabled: boolean): CardAuthorizationStatus | null {
  const key = orgId && enabled ? orgId : null;
  const [result, setResult] = useState<{ key: string; status: CardAuthorizationStatus } | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!key) return;
    let mounted = true;
    void fetchLiveCardAuthorizationStatus(key).then((status) => {
      if (mounted && status) setResult({ key, status });
    });
    return () => { mounted = false; };
  }, [key, tick]);

  const status = key && result?.key === key ? result.status : null;
  const awaits = status?.awaitsAuthorization === true;

  useEffect(() => {
    if (!key || !awaits) return;
    const recheck = () => setTick((n) => n + 1);
    const timer = window.setInterval(recheck, CARD_AUTHORIZATION_RECHECK_MS);
    window.addEventListener("focus", recheck);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", recheck);
    };
  }, [key, awaits]);

  return status;
}
